import { useTranslation } from 'react-i18next'
import type { CoinEntry, Match, Team } from '@brochure/shared'
import { reasonKey } from '@/lib/zambepong-format'
import { cn } from '@/lib/utils'

type CoinLedgerProps = { entries: CoinEntry[]; matches: Match[]; teams: Team[] }

// Solo questi motivi referenziano una partita (`refType: 'match'`, `refId` = id partita): cup/redemption/airball
// referenziano l'evento bicchiere, non la partita, quindi per loro l'avversario non è ricavabile da queste props.
const MATCH_LINKED_REASONS = new Set(['match_win', 'match_loss'])

/** Registro monete dell'ospite collegato: motivo tradotto e, quando ricavabile dalla partita, l'avversario. */
export function CoinLedger({ entries, matches, teams }: CoinLedgerProps) {
  const { t } = useTranslation()

  const teamOf = (participantId: string) => teams.find((team) => team.players.some((player) => player.id === participantId))

  function opponentName(entry: CoinEntry): string | null {
    if (!MATCH_LINKED_REASONS.has(entry.reason) || entry.refType !== 'match' || !entry.refId) return null
    const match = matches.find((m) => m.id === entry.refId)
    const myTeam = teamOf(entry.participantId)
    if (!match || !myTeam) return null
    const opponentId = match.homeTeamId === myTeam.id ? match.awayTeamId : match.homeTeamId
    return teams.find((team) => team.id === opponentId)?.name ?? null
  }

  if (entries.length === 0) return <p className="font-body text-sm text-ink-soft">{t('zambePong.coins.empty')}</p>

  // Il backend restituisce già le righe più recenti per prime (`ORDER BY createdAt DESC`).
  return (
    <ul data-testid="ledger" className="max-w-3xl divide-y divide-line/70 overflow-hidden rounded-xl border border-line bg-paper-deep/60 font-body text-sm">
      {entries.map((entry) => {
        const opponent = opponentName(entry)
        return (
          <li key={entry.id} className="flex min-h-12 items-center gap-3 px-4 py-2">
            <span
              className={cn(
                'w-14 shrink-0 rounded-md py-1 text-center font-semibold tabular-nums',
                entry.amount >= 0 ? 'bg-sea/15 text-sea-ink' : 'bg-coral/15 text-coral-ink',
              )}
            >
              {entry.amount > 0 ? '+' : ''}
              {entry.amount}
            </span>
            <span className="min-w-0 flex-1 truncate text-ink">{t(reasonKey(entry.reason))}</span>
            {opponent && <span className="shrink-0 truncate text-xs text-ink-soft">{opponent}</span>}
          </li>
        )
      })}
    </ul>
  )
}
